/// <reference path="./env.d.ts" />
/**
 * Configuração do app (§1.2, §2.5): fatos fixos da eleição (`ELECTION`), tipos e padrões de `AppConfig`, schema zod e
 * `resolveConfig()`. Importado também pelo appConfigPlugin (Node, no build): nada aqui toca DOM, `import.meta.env`
 * ou `__SIM_ENABLED__` fora de resolveConfig().
 */
import * as z from 'zod/mini';

/** §1.2: constantes do 2º turno; nenhum componente as redefine. */
export const ELECTION = Object.freeze({
  date: '2026-10-25',
  round: 2,
  firstRoundDate: '2026-10-04',
  /** 8h00 de Brasília. */
  pollsOpenTMin: 480,
  /** 17h00 de Brasília. */
  pollsCloseTMin: 1020,
  /** Liberação dos resultados (inclusive o Exterior). */
  resultsOpenTMin: 1020,
  /** UTC−3 fixo, sem horário de verão; conversões só em src/format/ e useNow(). */
  utcOffsetMin: -180,
  /** 22h00: fim padrão da trilha da linha do tempo. */
  nominalEndTMin: 1320,
} as const);

export interface FeatureFlags {
  governors: boolean;
  abroad: boolean;
  municipal_map: boolean;
  advantage_mode: boolean;
  zones: boolean;
  replay: boolean;
  tv: boolean;
  probability: boolean;
  archive_1t: boolean;
  partner_slots: boolean;
  presence: boolean;
  photos: boolean;
  share_image: boolean;
  social_links: boolean;
  governor_municipal: boolean;
  map_intro: boolean;
  /** Brilhos do mapa no canvas `fx` (FB-08, AC-9.17-04). Desligada em produção: ligar é decisão do responsável. */
  map_glow: boolean;
  /** Comentários ao vivo (P-32): ligada na publicação de até 23/10, com a confirmação do go/no-go (§8 de 15/10, item 7). */
  commentary: boolean;
  /**
   * Comentários nos replays (P-36, §8 de 16/10, 12h20, item 2): só na página do replay, separada de `commentary`.
   * Desligada por padrão; ligar em produção é PARE (§4.1-2).
   */
  replay_commentary: boolean;
  /**
   * Fotos oficiais dos candidatos nas noites do replay (ORD-F-022, item 2): só na página do replay, separada de
   * `photos` (as de 2026 do site ao vivo). A página do replay faz `photos = replay_photos` ao ler a configuração.
   */
  replay_photos: boolean;
  /**
   * A ponte do replay para o dia 25 (item 1.13 da síntese de 18/10, parte 1): a faixa "A apuração do 2º turno de 2026 é
   * no domingo, 25/10, às 17h00, em mapa.voto", com o lembrete na agenda, no hub e na tela da noite. Só na página do
   * replay; desligada por padrão (sem ela, nada muda no DOM); ligar em produção é PARE (§4.1-2).
   */
  replay_bridge: boolean;
  /**
   * Tela de comentários com linha do tempo no site ao vivo (ORD-F-026, PR 6): a troca "Apuração · Linha do tempo" e a
   * tela, só com os comentários ao vivo (`commentary`) também ligados. Desligada por padrão; ligar em produção é PARE
   * (§4.1-2), com o "ok" do responsável no go/no-go. No replay a tela vem com `replay_commentary`.
   */
  commentary_timeline: boolean;
  /**
   * Fases do site principal (ORD-F-054 item 2): a página inicial até 24/10 23h59:59, a pré-apuração de 25/10 00h00 às
   * 16h59:59 e a apuração às 17h00, trocadas pelo relógio da fonte, sem recarregar (`src/app/sitePhase.ts`). Desligada por
   * padrão (os testes da noite ficam como estão); ligar em produção é PARE (§4.1), com o "ok" do responsável.
   */
  site_phases: boolean;
  /**
   * ORD-F-066: a pré-apuração forte no lugar da sóbria, na fase `pre` (com `site_phases`). Desligada por padrão: a escolha
   * entre as duas é do responsável; ligar no ar é PARE (§4.1).
   */
  preopen_strong: boolean;
  /**
   * ORD-F-078/082 (D4): o cartão da definição matemática em tela cheia no site ao vivo (celular e computador), uma vez
   * por sessão por decisão (`src/app/mathCardSlot.tsx`). Desligada por padrão; ligar em produção é PARE (§4.1), depois da
   * aprovação dos textos (`src/copy/mathCard.ts`).
   */
  math_card: boolean;
  /**
   * Item 1.10 (ORD-F-078/082): abaixo de 1% das seções, as cores do mapa a 45% com a legenda "Ainda é cedo". Desligada
   * por padrão; ligar no ar é PARE.
   */
  early_veil: boolean;
  /**
   * Botão Compartilhar (ORD-F2-022 item 1.6; FRONT-2): texto pronto neutro e, com `share_image`, a imagem 1200×630 da D5.
   * Desligada por padrão; ligar em produção é PARE (§4.1). Nunca em simulação nem na TV.
   */
  share: boolean;
  /**
   * "Escolha o seu município" (D3; ORD-F2-022 PR "cidade"): o código IBGE guardado só no aparelho. Desligada por padrão;
   * ligar em produção é PARE, junto do /privacidade novo.
   */
  my_city: boolean;
  /**
   * ORD-F-153 (§8 de 20/10, decisão do responsável): a porta com login (Firebase Authentication pela API REST, sem SDK).
   * Sem sessão guardada no aparelho, a página principal pública (página inicial, pré-apuração e, a partir das 17h00, a
   * capa com a live), sem nenhum pedido ao feed; com sessão, o site completo. Desligada por padrão (o site de hoje);
   * docs/DECISOES.md §84.
   */
  login_gate: boolean;
  /**
   * ORD-F-153 item 10 (pacote `mapa-25-capa`): só a página principal pública, sem "Entrar", sem o app da apuração e sem
   * nenhum pedido ao feed, com ou sem sessão guardada. Desligada por padrão; docs/DECISOES.md §84.
   */
  capa_only: boolean;
}

export interface AppConfig {
  mode: 'live' | 'simulation';
  /** Lista para failover, cada uma terminando em "/". */
  feed_origins: string[];
  /** "<sha7>-<AAAAMMDDHHmm UTC>": único por deploy. */
  build: string;
  poll_interval_s: number;
  /** No site ao vivo, sempre `ELECTION.date` e `ELECTION.round` (o schema os trava); no replay, os da noite aberta. */
  election_date: string;
  round: 1 | 2;
  /** P2: origem https:// do endpoint de presença (§6.11); null = sem presença. */
  presence_origin: string | null;
  /** P2: perfis próprios (§6.2); null = sem link. */
  social_profiles: { x: string | null; instagram: string | null };
  features: FeatureFlags;
  /**
   * Início da abertura pós-apuração (fase `after`, pedido de 26/10; src/app/phaseClock.ts), "AAAA-MM-DDTHH:MM[:SS]±HH:MM".
   * Ausente ou ilegível = 06h00 de Brasília do dia seguinte à eleição. Só vale com `features.site_phases`.
   */
  after_at?: string;
}

/** Coluna "Padrão (produção)" de §2.5.1. */
export const DEFAULT_FEATURES: Readonly<FeatureFlags> = Object.freeze({
  governors: true,
  abroad: true,
  municipal_map: true,
  advantage_mode: false,
  zones: false,
  replay: true,
  tv: true,
  probability: false,
  archive_1t: false,
  partner_slots: false,
  presence: false,
  photos: true,
  share_image: false,
  social_links: false,
  governor_municipal: false,
  map_intro: false,
  map_glow: false,
  commentary: false,
  replay_commentary: false,
  replay_photos: false,
  replay_bridge: false,
  commentary_timeline: false,
  site_phases: false,
  preopen_strong: false,
  math_card: false,
  early_veil: false,
  share: false,
  my_city: false,
  login_gate: false,
  capa_only: false,
});

export const FEATURE_NAMES = Object.keys(DEFAULT_FEATURES) as (keyof FeatureFlags)[];

/** Congela o objeto e tudo dentro dele (§2.5.2 passo 4). */
export function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const key of Object.keys(value)) deepFreeze((value as Record<string, unknown>)[key]);
  }
  return value;
}

/**
 * Passo 1 de §2.5.2 (= coluna "Padrão"). Usado inteiro quando `#app-config` falta ou é inválido. O `build` sentinela
 * segue o formato de §2.5.1 para nunca coincidir com um build real (ops.reload_build recarrega uma vez, §14.8).
 */
export const DEFAULT_CONFIG: Readonly<AppConfig> = deepFreeze<AppConfig>({
  mode: 'live',
  feed_origins: ['https://feed.mapa.voto/feed/v1/'],
  build: '0000000-000000000000',
  poll_interval_s: 15,
  election_date: ELECTION.date,
  round: ELECTION.round,
  presence_origin: null,
  social_profiles: { x: null, instagram: null },
  features: { ...DEFAULT_FEATURES },
});

// ── Schema (§2.5.1) ──────────────────────────────────────────────────────────────────────────────────────────
/** URL `https://` ou null; outro texto vira null (§2.5.1, `social_profiles`/`presence_origin`), sem invalidar o resto. */
const HttpsOrNull = z.pipe(
  z.nullable(z.string()),
  z.transform((v: string | null) => (v?.startsWith('https://') ? v : null)),
);
/** Origem absoluta https://, raiz relativa (/feed/v1/, dev e CI) ou http do host local (testes). */
const FEED_ORIGIN_RE = /^(https:\/\/[^\s]+|\/[^\s]*|http:\/\/(localhost|127\.0\.0\.1|\[::1\])(:\d+)?\/[^\s]*)$/;

export const FeatureFlagsSchema = z.object({
  governors: z.boolean(),
  abroad: z.boolean(),
  municipal_map: z.boolean(),
  advantage_mode: z.boolean(),
  zones: z.boolean(),
  replay: z.boolean(),
  tv: z.boolean(),
  probability: z.boolean(),
  archive_1t: z.boolean(),
  partner_slots: z.boolean(),
  presence: z.boolean(),
  photos: z.boolean(),
  share_image: z.boolean(),
  social_links: z.boolean(),
  governor_municipal: z.boolean(),
  map_intro: z.boolean(),
  map_glow: z.boolean(),
  // chave nova (P-32): configuração gravada antes dela continua válida, com os comentários desligados
  commentary: z._default(z.boolean(), false),
  // chave nova (P-36): idem, com os comentários do replay desligados
  replay_commentary: z._default(z.boolean(), false),
  replay_photos: z._default(z.boolean(), false),
  // chave nova (item 1.13): configuração gravada antes dela continua válida, com a ponte desligada
  replay_bridge: z._default(z.boolean(), false),
  // chave nova (ORD-F-026, PR 6): configuração gravada antes dela continua válida, com a tela desligada
  commentary_timeline: z._default(z.boolean(), false),
  // chave nova (ORD-F-054 item 2): configuração gravada antes dela continua válida, com as fases desligadas
  site_phases: z._default(z.boolean(), false),
  // chave nova (ORD-F-066): configuração gravada antes dela continua válida, com a pré-apuração sóbria
  preopen_strong: z._default(z.boolean(), false),
  // chave nova (ORD-F-078/082, D4): configuração gravada antes dela continua válida, sem o cartão
  math_card: z._default(z.boolean(), false),
  // chave nova (item 1.10): configuração gravada antes dela continua válida, sem o véu
  early_veil: z._default(z.boolean(), false),
  // chave nova (ORD-F2-022 item 1.6): configuração gravada antes dela continua válida, com o botão desligado
  share: z._default(z.boolean(), false),
  // chave nova (ORD-F2-022 PR "cidade"): configuração gravada antes dela continua válida, com o campo desligado
  my_city: z._default(z.boolean(), false),
  // chaves novas (ORD-F-153): configuração gravada antes delas continua válida, sem a porta e sem a capa
  login_gate: z._default(z.boolean(), false),
  capa_only: z._default(z.boolean(), false),
});

export const AppConfigSchema = z.object({
  mode: z.enum(['live', 'simulation']),
  feed_origins: z.array(z.string().check(z.regex(FEED_ORIGIN_RE))).check(z.minLength(1)),
  build: z.string().check(z.minLength(1)),
  poll_interval_s: z.number().check(z.positive()),
  election_date: z.literal(ELECTION.date),
  round: z.literal(ELECTION.round),
  presence_origin: HttpsOrNull,
  social_profiles: z.object({ x: HttpsOrNull, instagram: HttpsOrNull }),
  features: FeatureFlagsSchema,
  // campo novo (26/10): configuração gravada antes dele continua válida, com a abertura pós-apuração no padrão
  after_at: z.optional(z.string()),
});

// ── resolveConfig (§2.5.2) ───────────────────────────────────────────────────────────────────────────────────
export interface ResolveConfigOptions {
  /** Onde procurar `#app-config` (padrão: `document`). */
  doc?: Pick<Document, 'getElementById'> | null;
  /** Query real (`location.search`; padrão: a da página). O hash nunca é lido. */
  search?: string;
  /** `__SIM_ENABLED__` (padrão: a constante de build; `false` em produção). */
  simEnabled?: boolean;
  /** Relógio para a trava de data do parceiro (padrão: `Date.now()`). */
  nowMs?: number;
  /** Registro do erro de configuração (padrão: `console.error`). */
  onError?: (message: string, error: unknown) => void;
}

/**
 * "+zones,-photos" → { zones: true, photos: false } (§14.9.7). Na query, "+" chega decodificado como espaço; nome sem
 * sinal liga. Nome desconhecido é ignorado.
 */
export function parseQueryFlags(value: string | null | undefined): Partial<FeatureFlags> {
  const out: Partial<FeatureFlags> = {};
  for (const item of (value ?? '').split(',')) {
    const m = /^\s*([+-]?)\s*([a-z0-9_]+)\s*$/.exec(item);
    if (!m) continue;
    const name = m[2] as keyof FeatureFlags;
    if (FEATURE_NAMES.includes(name)) out[name] = m[1] !== '-';
  }
  return out;
}

/** Data de hoje em Brasília (UTC−3 fixo), "AAAA-MM-DD". */
/**
 * Trava fixa do slot de parceiro no dia da votação (AC-1.6-03, E05). Desde a ORD-F-134 (D1 do responsável: "o banner
 * aparece no dia 25 se houver contrato assinado até 22/10 às 18h"), no dia 25 decide a chave `partner_slots`, como nos
 * outros dias: desligada, nada aparece; ligar é ordem do responsável no pacote do dia 25. Para voltar à trava fixa
 * (sem contrato ou se ele mudar de ideia), basta `true` aqui (docs/DECISOES.md §77).
 */
export const PARTNER_LOCKED_ON_ELECTION_DAY: boolean = false;

export function brasiliaDate(nowMs: number): string {
  return new Date(nowMs + ELECTION.utcOffsetMin * 60_000).toISOString().slice(0, 10);
}

function readJson(doc: Pick<Document, 'getElementById'> | null | undefined): unknown {
  const el = doc?.getElementById('app-config');
  if (!el) throw new Error('#app-config ausente');
  const text = el.textContent ?? '';
  if (text.trim() === '') throw new Error('#app-config vazio');
  return JSON.parse(text);
}

function simEnabledDefault(): boolean {
  return typeof __SIM_ENABLED__ !== 'undefined' && __SIM_ENABLED__;
}

/**
 * Resolve a configuração uma vez, em main.tsx (§2.5.2): padrão < `#app-config` < query (só com `__SIM_ENABLED__`) <
 * regras derivadas. Nunca lança: config ausente ou inválida vira `DEFAULT_CONFIG` com `console.error('[config] inválida')`.
 * O resultado é congelado em profundidade e gravado no sinal `config` por main.tsx.
 */
export function resolveConfig(opts: ResolveConfigOptions = {}): AppConfig {
  const doc = 'doc' in opts ? opts.doc : typeof document === 'undefined' ? null : document;
  const onError = opts.onError ?? ((message: string, error: unknown) => console.error(message, error));
  // 1–2. Padrão, depois o JSON do build (inteiro ou nada).
  let cfg: AppConfig;
  try {
    const parsed = AppConfigSchema.safeParse(readJson(doc));
    if (!parsed.success) throw parsed.error;
    cfg = { ...parsed.data, feed_origins: [...parsed.data.feed_origins] };
  } catch (error) {
    onError('[config] inválida', error);
    cfg = cloneConfig(DEFAULT_CONFIG);
  }
  // 3. Query real, só fora de produção (§14.9.7): `projecao` e `flags` (os demais parâmetros são de createSource).
  const simEnabled = opts.simEnabled ?? simEnabledDefault();
  if (simEnabled) {
    const search = opts.search ?? (typeof location === 'undefined' ? '' : location.search);
    const query = new URLSearchParams(search);
    const features = { ...cfg.features };
    const projection = query.get('projecao');
    if (projection === '1' || projection === '0') features.probability = projection === '1';
    Object.assign(features, parseQueryFlags(query.get('flags')));
    cfg = { ...cfg, features };
  }
  // 4. Regras derivadas, sempre por último.
  const nowMs = opts.nowMs ?? Date.now();
  if (PARTNER_LOCKED_ON_ELECTION_DAY && brasiliaDate(nowMs) === cfg.election_date)
    cfg = { ...cfg, features: { ...cfg.features, partner_slots: false } };
  cfg = { ...cfg, feed_origins: cfg.feed_origins.map((o) => (o.endsWith('/') ? o : `${o}/`)) };
  return deepFreeze(cfg);
}

function cloneConfig(c: Readonly<AppConfig>): AppConfig {
  return {
    ...c,
    feed_origins: [...c.feed_origins],
    social_profiles: { ...c.social_profiles },
    features: { ...c.features },
  };
}
